import { useIsFetching as useTanStackIsFetching } from '@tanstack/react-query';

const contains = (array1: string[], array2: string[]) =>
    array1.some((value: string) => array2.includes(value));

/**
 * A custom hook that checks if any of the specified queries are currently fetching.
 *
 * @param {string[]} queryNames - An array of query names to check for fetching status.
 *                                These names should match part of the queryKey used
 *                                when defining the queries.
 *
 * @returns {boolean} Returns true if any of the specified queries are currently fetching,
 *                    false otherwise.
 *
 * @example
 * const queryDependencies = ['instant-cash', 'investments/advertisement', 'extensions/loyalty'];
 * const isFetching = useIsFetching(queryDependencies);
 *
 * if (isFetching) {
 *   return <LoadingSpinner />;
 * }
 *
 * @example
 * // In a component
 * function MyComponent() {
 *   const isLoading = useIsFetching(['user-profile', 'user-settings']);
 *
 *   return (
 *     <div>
 *       {isLoading ? 'Loading...' : 'Data loaded!'}
 *     </div>
 *   );
 * }
 *
 */
export function useIsFetching(queryNames: string[]): boolean {
    return (
        useTanStackIsFetching({
            predicate: (query) => contains(query.queryKey as string[], queryNames),
        }) > 0
    );
}
